import moment from 'moment';
/**
 * Converts a duration value and type to ISO 8601 duration format using moment.js
 * @param value - The duration value (string)
 * @param type - The duration type ('minute' | 'hours')
 * @returns ISO 8601 duration string (e.g., "PT15M", "PT2H", "PT1H30M")
 *
 * @example
 * convertToISODuration('15', 'minute') // "PT15M"
 * convertToISODuration('2', 'hours') // "PT2H"
 * convertToISODuration('90', 'minute') // "PT1H30M"
 * convertToISODuration('1.5', 'hours') // "PT1H30M"
 */
export function convertToISODuration(value, timeType) {
    // Convert to number and validate
    var numValue = parseFloat(value);
    if (isNaN(numValue) || numValue < 0) {
        throw new Error('Invalid duration value');
    }
    var duration = moment.duration(numValue, timeType);
    return duration.toISOString();
}
/**
 * Converts an ISO 8601 duration string back to minutes using moment.js
 * @param isoDuration - ISO 8601 duration string (e.g., "PT15M", "PT2H", "PT1H30M")
 * @returns Duration in minutes
 *
 * @example
 * convertFromISODuration("PT15M") // 15
 * convertFromISODuration("PT2H") // 120
 * convertFromISODuration("PT1H30M") // 90
 */
export function convertFromISODuration(isoDuration, timeType) {
    try {
        var duration = moment.duration(isoDuration);
        return timeType === 'minutes' ? duration.asMinutes() : duration.asHours();
    }
    catch (error) {
        return undefined;
    }
}
/**
 * Formats a duration in minutes to a human-readable string using moment.js
 * @param minutes - Duration in minutes
 * @returns Human-readable duration string
 *
 * @example
 * formatDuration(15) // "15 minutes"
 * formatDuration(60) // "1 hour"
 * formatDuration(90) // "1 hour 30 minutes"
 */
export function formatDuration(minutes, timeType) {
    var duration = moment.duration(minutes, timeType);
    return duration.humanize();
}
/**
 * For API payloads: treat ISO-8601 durations that parse to zero length (or
 * invalid / empty) as `null`, so we do not send `PT0M`, `P0D`, `PT0S`, etc.
 * Non-zero durations return the trimmed input string unchanged.
 */
export function isoDurationOrNullIfZero(iso) {
    if (iso === undefined || iso === null) {
        return null;
    }
    var trimmed = String(iso).trim();
    if (trimmed === '') {
        return null;
    }
    var duration = moment.duration(trimmed);
    var ms = duration.asMilliseconds();
    if (!Number.isFinite(ms) || ms <= 0) {
        return null;
    }
    return trimmed;
}
